// react
import { type ReactNode, useMemo, useState } from 'react';

// relative
import {
  BreadcrumbContext,
  type BreadcrumbContextValue,
} from './BreadcrumbContext';

/**
 * Holds dynamic breadcrumb label overrides (see `useBreadcrumb`), keyed by
 * TanStack Router match id. Must wrap both `AppBar` (which renders
 * `AppBreadcrumbs`) and the routed `Outlet` (whose page components call
 * `useBreadcrumb`) - see `src/routes/__root.tsx`.
 */
export default function BreadcrumbProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const value = useMemo<BreadcrumbContextValue>(
    () => ({
      overrides,
      setOverride: (matchId, label) => {
        setOverrides((prev) =>
          prev[matchId] === label ? prev : { ...prev, [matchId]: label },
        );
      },
      clearOverride: (matchId) => {
        setOverrides((prev) => {
          if (!(matchId in prev)) return prev;
          const next = { ...prev };
          delete next[matchId];
          return next;
        });
      },
    }),
    [overrides],
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
}
